<!--
 * A Vue component for username and password.
 *
 * Properties
 * ==========
 * - required -
 * (Optional) A flag to set the input fields to required.
 * Just set word 'required' on the this component from parent component.
 *
 * - bold -
 * (Optional) A flag to set the label of input fields to bold.
 * Just set word 'bold' on the this component from parent component.
 *
 * - username -
 * (Optional) Sets the initial value of input username.
 *
 * - password -
 * (Optional) Sets the initial value of input password.
 *
 * - maxlength -
 * (Optional) To set the maxlength of the input elements.
 *
 * - epattern -
 * (Optional) The pattern to check the e-mail input against.
 *
 * - pwpattern -
 * (Optional) The pattern to check the password input against.
 *
 * - pwtogglevisible -
 * (Optional) True if password toggle must be shown. False of no toggle must be shown.
 *
 * Events
 * ==========
 * - inputChanged -
 * This event is sent when user changes the input value.
 * The object to sent along with event has two properties, namely username and password.
 *
-->
<template>
    <div>
        <div v-if="!hideUsername" class="form-group">
            <b-form-group
                class="form-group"
                :state="userStateValid"
                :invalid-feedback="feedbackuserInUseMessage"
            >
                <label ref="label1" for="exampleInputEmail1">
                    {{ $t('labelUsername') }}
                </label>
                <b-form-input
                    type="text"
                    class="form-control"
                    :id="`${idPrefix}username`"
                    :value="usernameValue"
                    @input="_onInputName"
                    :required="required"
                    :maxlength="maxlength"
                    :pattern="epattern"
                    :state="userStateValid"
                    autocomplete="new-password"
                    :readonly="readonly || userNameReadonly"
                >
                </b-form-input>
            </b-form-group>
        </div>
        <div>
            <label ref="label2" class="label-position">
                {{ $t('labelPassword') }}
            </label>
            <div class="input-group">
                <base-input
                    :id="`${idPrefix}password`"
                    :type="inputType"
                    extendedClass="search form-control"
                    v-model="passwordValue"
                    @input="_onInput"
                    :required="required"
                    :maxlength="maxlength"
                    :pattern="pwpattern"
                    class="max_width"
                    autocomplete="new-password"
                    :readonly="readonly || passwordReadonly"
                >
                </base-input>
                <b-button
                    v-if="pwtogglevisible"
                    class="btn-field no-action"
                    @click="_toggleType"
                >
                    <em
                        :class="[eyeIcon ? 'fa fa-eye' : 'fa fa-eye-slash']"
                    ></em>
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
    import { trimAsterisks } from './../services/utilities';
    import { INPUT_TYPE } from '../constants';
    import BaseInput from './BaseComponents/base-input.vue';

    export default {
        name: 'username-password',

        components: { 'base-input': BaseInput },

        props: {
            idPrefix: { type: String, required: false, default: '' },
            hideUsername: { type: Boolean, required: false, default: false },
            required: { type: Boolean, required: false },
            bold: { type: String, required: false },
            username: { type: String, required: false, default: '' },
            password: { type: String, required: false, default: '' },
            maxlength: { type: Number, required: false, default: 50 },
            epattern: { type: String, required: false },
            pwpattern: { type: String, required: false },
            pwtogglevisible: { type: Boolean, required: false, default: true },
            userStateValid: { type: Boolean, required: false, default: true },
            pwIsSaved: { type: Boolean, required: false, default: false },
            readonly: { type: Boolean, required: false, default: false },
            userNameReadonly: { type: Boolean, required: false, default: false },
            passwordReadonly: { type: Boolean, required: false, default: false }
        },

        data() {
            return {
                eyeIcon: false,
                feedbackuserInUseMessage: this.$t(
                    'organizationFeedbackUserNameInUse'
                ),
                inputType: INPUT_TYPE.Password,
                localPassword: ''
            };
        },

        computed: {
            usernameValue() {
                return this.username;
            },
            passwordValue: {
                get() {
                    return this.localPassword || this.password;
                },
                set(input) {
                    return input;
                }
            }
        },

        mounted() {
            this._fontWeight();
        },

        methods: {
            _fontWeight() {
                if (this.bold !== undefined) {
                    this.$refs.label1.style.fontWeight = 'bold';
                    this.$refs.label2.style.fontWeight = 'bold';
                }
                // to prevent postback
                return false;
            },

            _toggleType() {
                this.inputType =
                    this.inputType === INPUT_TYPE.Text
                        ? INPUT_TYPE.Password
                        : INPUT_TYPE.Text;
                this.eyeIcon = this.inputType === INPUT_TYPE.Text;
            },
            _onInput(event) {
                // Remove all * from input
                const trimmedPassword = trimAsterisks(event);

                // If the trimmed password is not empty then it contains other characters then only *
                // In that case we take the entire inpit string (password may contain a *)
                if (trimmedPassword) {
                    this.localPassword = event;
                } else {
                    this.localPassword = '';
                }
                this.$emit('inputchanged', this.passwordValue);
            },
            _onInputName(input) {
                this.$emit('inputnamechanged', input);
            }
        },

        watch: {
            /**
             * Masks the input when data is saved
             * @param newValue
             */
            pwIsSaved(newValue) {
                if (newValue && this.inputType === INPUT_TYPE.Text) {
                    this._toggleType();
                }
            }
        }
    };
</script>

<style scoped>
.btn-field {
    color: #212529;
    overflow: visible;
    border: 0;
    padding: 0;
    cursor: pointer;
    width: 20px;
    height: 20px;
    position: absolute;
    z-index: 4;
    right: 26px;
    top: 7px;
    background: transparent;
}

.btn-field .no-action {
    cursor: text;
}

.label-position {
    left: 30px;
    position: sticky;
}

.max_width {
    width: 100%;
}
</style>
